import { z } from "zod";
import { accountTypeSchema, accountVisibilitySchema, cadenceSchema, categoryKindSchema, confirmationPolicySchema, committeePayoutModeSchema, financialEventStatusSchema, financialEventTypeSchema, timelineScopeSchema, transactionTypeSchema, } from "./enums.js";
import { amountMinorSchema, currencySchema, DEFAULT_CURRENCY } from "./money.js";
/** A v4/v7 UUID as produced by Postgres `gen_random_uuid()`. */
export const uuidSchema = z.string().uuid();
export const emailSchema = z.string().trim().toLowerCase().email().max(254);
export const usernameSchema = z
    .string()
    .trim()
    .min(3)
    .max(32)
    .regex(/^[a-z0-9_]+$/i, "username may contain letters, numbers and underscore");
export const passwordSchema = z.string().min(8).max(200);
// ─── Auth ─────────────────────────────────────────────────────────────────────
export const registerSchema = z.object({
    name: z.string().trim().min(1).max(120),
    username: usernameSchema,
    email: emailSchema,
    password: passwordSchema,
});
export const loginSchema = z
    .object({
    username: usernameSchema.optional(),
    email: emailSchema.optional(),
    password: z.string().min(1),
})
    .refine((v) => Boolean(v.username || v.email), {
    message: "username or email is required",
});
export const refreshSchema = z.object({
    refreshToken: z.string().min(1).optional(), // may instead arrive via httpOnly cookie
});
export const verifyEmailSchema = z.object({ token: z.string().min(1) });
export const forgotPasswordSchema = z.object({ email: emailSchema });
export const resetPasswordSchema = z.object({
    token: z.string().min(1),
    password: passwordSchema,
});
export const publicUserSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    username: z.string(),
    email: z.string(),
    status: z.string(),
    emailVerified: z.boolean(),
    defaultCurrency: z.string(),
});
export const authResultSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string().optional(),
    user: publicUserSchema,
});
// ─── Accounts ───────────────────────────────────────────────────────────────
export const createAccountSchema = z.object({
    name: z.string().trim().min(1).max(80),
    type: accountTypeSchema,
    currency: currencySchema.default(DEFAULT_CURRENCY),
    openingBalanceMinor: z.number().int().default(0),
    visibility: accountVisibilitySchema.default("private"),
    description: z.string().trim().max(500).optional(),
});
export const updateAccountSchema = createAccountSchema.partial();
export const updateAccountVisibilitySchema = z.object({
    visibility: accountVisibilitySchema,
});
// ─── Connections (mutual) & user discovery ────────────────────────────────────
/** Look up people by (partial) username. */
export const userSearchSchema = z.object({
    q: z.string().trim().min(1).max(32),
});
/** Send a connection request to a username. */
export const connectionRequestSchema = z.object({
    username: usernameSchema,
});
/** Accept or reject an incoming connection request. */
export const respondConnectionSchema = z.object({ accept: z.boolean() });
// ─── Categories ───────────────────────────────────────────────────────────────
export const createCategorySchema = z.object({
    kind: categoryKindSchema,
    name: z.string().trim().min(1).max(60),
    icon: z.string().trim().max(60).optional(),
    color: z
        .string()
        .trim()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .optional(),
});
// ─── Transactions ─────────────────────────────────────────────────────────────
/** A witness can be referenced by an existing userId or invited by email. */
export const witnessRefSchema = z
    .object({
    userId: uuidSchema.optional(),
    email: emailSchema.optional(),
    name: z.string().trim().max(120).optional(),
})
    .refine((v) => Boolean(v.userId || v.email), {
    message: "witness needs a userId or an email",
});
/** Personal income/expense: affects a single owned account. */
export const createSimpleTransactionSchema = z.object({
    type: z.enum(["income", "expense"]),
    accountId: uuidSchema,
    amountMinor: amountMinorSchema,
    currency: currencySchema.default(DEFAULT_CURRENCY),
    categoryId: uuidSchema.optional(),
    description: z.string().trim().max(500).optional(),
    occurredAt: z.coerce.date().optional(),
});
/** Internal transfer between two of your OWN accounts (no witnesses). */
export const createTransferSchema = z.object({
    senderAccountId: uuidSchema,
    receiverAccountId: uuidSchema,
    amountMinor: amountMinorSchema,
    currency: currencySchema.default(DEFAULT_CURRENCY),
    description: z.string().trim().max(500).optional(),
    occurredAt: z.coerce.date().optional(),
});
// ─── Witness confirmation (public, token-based) ───────────────────────────────
export const witnessActionSchema = z.object({
    token: z.string().min(1),
    action: z.enum(["confirm", "decline"]),
});
/** Logged-in witness confirms/declines a loan request from the in-app inbox. */
export const witnessRespondSchema = z.object({
    action: z.enum(["confirm", "decline"]),
});
// ─── Loans ──────────────────────────────────────────────────────────────────
export const createLoanSchema = z
    .object({
    direction: z.enum(["lent", "borrowed"]).default("lent"),
    counterpartyUserId: uuidSchema.optional(),
    counterpartyUsername: usernameSchema.optional(),
    counterpartyEmail: emailSchema.optional(),
    counterpartyName: z.string().trim().max(120).optional(),
    // The lender's account that is debited when the loan ACTIVATES. Required
    // when you are the lender (direction 'lent'); when borrowing it's set by the
    // counterparty (the lender) on acceptance. Optional here, validated server-side.
    accountId: uuidSchema.optional(),
    principalMinor: amountMinorSchema,
    currency: currencySchema.default(DEFAULT_CURRENCY),
    dueDate: z.coerce.date().optional(),
    description: z.string().trim().max(500).optional(),
    // Optional witnesses who must confirm (after the counterparty accepts)
    // before the loan activates. Witnesses apply to loans only.
    requiresWitness: z.boolean().default(false),
    witnesses: z.array(witnessRefSchema).max(10).optional(),
    // Optional FX override (loan currency → a foreign account currency).
    fxRate: z.number().positive().optional(),
})
    .refine((v) => Boolean(v.counterpartyUserId || v.counterpartyUsername || v.counterpartyEmail), { message: "a counterparty userId, username or email is required" });
/** Add one or more witnesses to an existing loan (before it activates). */
export const addLoanWitnessesSchema = z.object({
    witnesses: z.array(witnessRefSchema).min(1).max(10),
});
/** Counterparty accepts/rejects a pending loan request. */
export const respondLoanSchema = z.object({
    accept: z.boolean(),
    // When the accepting party is the lender (you were asked to lend), the account
    // to disburse from.
    accountId: uuidSchema.optional(),
});
/**
 * Propose a repayment on an active loan. The initiator names THEIR OWN account
 * (a borrower's is debited, a lender's is credited); the counterparty confirms
 * with their account before the legs post.
 */
export const repayLoanSchema = z.object({
    accountId: uuidSchema,
    amountMinor: amountMinorSchema,
    // When the money actually changed hands. The request itself may be recorded
    // later, so this is intentionally separate from the row's createdAt audit time.
    paidAt: z.coerce.date().optional(),
});
/** Counterparty confirms/declines a pending repayment, naming their own account. */
export const respondRepaymentSchema = z.object({
    accept: z.boolean(),
    accountId: uuidSchema.optional(),
});
// ─── Recurring rules ──────────────────────────────────────────────────────────
export const createRecurringSchema = z.object({
    kind: categoryKindSchema,
    amountMinor: amountMinorSchema,
    accountId: uuidSchema.optional(),
    categoryId: uuidSchema.optional(),
    cadence: z.enum(["weekly", "biweekly", "monthly", "quarterly", "yearly"]).default("monthly"),
    dayOfMonth: z.number().int().min(1).max(31).optional(),
    startDate: z.coerce.date().optional(),
    endDate: z.coerce.date().optional(),
    // false = occurrences appear as pending rows to mark done; true = auto-post.
    autoConfirm: z.boolean().default(false),
    description: z.string().trim().max(500).optional(),
});
// ─── Committees (ROSCA / BC) ──────────────────────────────────────────────────
export const createCommitteeSchema = z.object({
    name: z.string().trim().min(1).max(120),
    contributionAmountMinor: amountMinorSchema,
    currency: currencySchema.default(DEFAULT_CURRENCY),
    frequency: cadenceSchema.default("monthly"),
    totalSlots: z.number().int().min(2).max(50),
    payoutMode: committeePayoutModeSchema.default("lottery"),
    startDate: z.coerce.date().optional(),
    // The organizer's own accounts: contributions paid FROM, payouts received INTO.
    contributionAccountId: uuidSchema.optional(),
    payoutAccountId: uuidSchema.optional(),
});
export const inviteMemberSchema = z
    .object({
    userId: uuidSchema.optional(),
    username: usernameSchema.optional(),
    email: emailSchema.optional(),
    name: z.string().trim().max(120).optional(),
})
    .refine((v) => Boolean(v.userId || v.username || v.email), {
    message: "userId, username or email required",
});
export const respondInviteSchema = z.object({
    accept: z.boolean(),
    // On accept, the member picks where contributions come from / payouts go to.
    contributionAccountId: uuidSchema.optional(),
    payoutAccountId: uuidSchema.optional(),
});
export const drawRoundSchema = z.object({
    // For 'agreed' / 'fixed' modes the organizer may pass the chosen member.
    memberId: uuidSchema.optional(),
});
// accountId optional — falls back to the member's chosen contribution account.
export const contributeSchema = z.object({ accountId: uuidSchema.optional() });
// ─── Forecast ─────────────────────────────────────────────────────────────────
export const forecastQuerySchema = z.object({
    months: z.coerce.number().int().min(1).max(24).default(6),
});
// ─── Profile ──────────────────────────────────────────────────────────────────
/** Update the editable profile fields. Username is immutable and not accepted. */
export const updateProfileSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    defaultCurrency: currencySchema.optional(),
});
/**
 * Initiate an email-confirmed password change while logged in. We verify the
 * current password, then email a confirmation link; the new password is set via
 * the existing reset-password flow.
 */
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1),
});
// ─── Common ───────────────────────────────────────────────────────────────────
export const paginationSchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().optional(),
});
/**
 * Optional month (1-12) and year filter shared by list endpoints. A year alone
 * selects that full year; both omitted means no period filter.
 */
export const periodQuerySchema = z.object({
    month: z.coerce.number().int().min(1).max(12).optional(),
    year: z.coerce.number().int().min(2000).max(2100).optional(),
});
/** Transactions list query: pagination + optional period + optional account. */
export const transactionsQuerySchema = paginationSchema.merge(periodQuerySchema).extend({
    // Filter to transactions where this account is the sender or receiver.
    accountId: uuidSchema.optional(),
});
export const idParamSchema = z.object({ id: uuidSchema });
// ─── V2 Financial timelines ──────────────────────────────────────────────────
export const timelineToneSchema = z.enum(["neutral", "positive", "negative", "warning"]);
export const timelineSourceSchema = z.enum([
    "financial_event",
    "transaction",
    "loan",
    "loan_repayment",
]);
export const timelineActionKindSchema = z.enum([
    "create_loan",
    "accept_loan",
    "decline_loan",
    "repay_loan",
    "confirm_repayment",
    "decline_repayment",
    "request_undo",
    "approve_undo",
    "decline_undo",
    "confirm_event",
    "reject_event",
    "dispute_event",
    "attach_receipt",
    "ask_ai",
    "open_details",
]);
export const timelineActionRequirementSchema = z.enum(["account", "amount", "note", "receipt"]);
export const timelineActionSchema = z.object({
    kind: timelineActionKindSchema,
    label: z.string().trim().min(1).max(80),
    style: z.enum(["primary", "secondary", "danger"]).default("secondary"),
    method: z.enum(["GET", "POST", "PATCH", "DELETE"]).default("POST"),
    path: z.string().trim().min(1).max(240).optional(),
    body: z.record(z.unknown()).optional(),
    requires: z.array(timelineActionRequirementSchema).max(4).default([]),
    disabledReason: z.string().trim().max(160).optional(),
});
export const timelineBlockSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("summary"),
        label: z.string().min(1).max(120),
        value: z.string().min(1).max(160),
        tone: timelineToneSchema.default("neutral"),
    }),
    z.object({
        kind: z.literal("chart"),
        chartType: z.enum(["line", "bar", "donut", "stacked_bar"]),
        title: z.string().min(1).max(160),
        series: z
            .array(z.object({
            name: z.string().min(1).max(80),
            colorToken: z.string().trim().max(40).optional(),
            points: z.array(z.object({ x: z.string().min(1), y: z.number() })).max(500),
        }))
            .min(1)
            .max(12),
        xAxisLabel: z.string().trim().max(80).optional(),
        yAxisLabel: z.string().trim().max(80).optional(),
    }),
    z.object({
        kind: z.literal("table"),
        title: z.string().trim().max(160).optional(),
        columns: z
            .array(z.object({
            key: z.string().trim().min(1).max(80),
            label: z.string().trim().min(1).max(80),
            align: z.enum(["left", "right"]).default("left"),
        }))
            .min(1)
            .max(16),
        rows: z.array(z.record(z.union([z.string(), z.number()]))).max(200),
    }),
    z.object({
        kind: z.literal("image"),
        attachmentId: uuidSchema,
        thumbnailUrl: z.string().trim().min(1).max(500),
        fullUrl: z.string().trim().min(1).max(500),
        caption: z.string().trim().max(160).optional(),
    }),
    z.object({
        kind: z.literal("action"),
        action: z.enum([
            "confirm",
            "reject",
            "dispute",
            "attach_receipt",
            "repay",
            "mark_settled",
            "undo",
        ]),
        label: z.string().trim().min(1).max(80),
        style: z.enum(["primary", "secondary", "danger"]).default("secondary"),
        targetPath: z.string().trim().min(1).max(240).optional(),
        requires: z.array(timelineActionRequirementSchema).max(4).default([]),
    }),
]);
export const timelineSummarySchema = z.object({
    label: z.string().trim().min(1).max(80),
    value: z.string().trim().min(1).max(160),
    tone: timelineToneSchema.default("neutral"),
});
export const timelineContextSchema = z.object({
    scope: timelineScopeSchema,
    id: uuidSchema.optional(),
    title: z.string().trim().min(1).max(160),
    subtitle: z.string().trim().max(240).nullable().default(null),
    primaryCurrency: currencySchema.optional(),
    summaries: z.array(timelineSummarySchema).max(8).default([]),
    actions: z.array(timelineActionSchema).max(8).default([]),
});
export const timelineItemSchema = z.object({
    id: z.string().trim().min(1).max(120),
    source: timelineSourceSchema,
    sourceId: uuidSchema,
    type: z.string().trim().min(1).max(80),
    status: z.string().trim().min(1).max(80),
    title: z.string().trim().min(1).max(160),
    subtitle: z.string().trim().max(300).nullable(),
    amountMinor: z.number().int().nullable(),
    currency: currencySchema.nullable(),
    tone: timelineToneSchema,
    occurredAt: z.string().datetime(),
    createdAt: z.string().datetime(),
    fromAccountId: uuidSchema.nullable(),
    fromAccountName: z.string().nullable(),
    toAccountId: uuidSchema.nullable(),
    toAccountName: z.string().nullable(),
    counterpartyUserId: uuidSchema.nullable(),
    counterpartyName: z.string().nullable(),
    loanId: uuidSchema.nullable(),
    transactionId: uuidSchema.nullable(),
    blocks: z.array(timelineBlockSchema).default([]),
    actions: z.array(timelineActionSchema).default([]),
    metadata: z.record(z.unknown()).default({}),
});
export const timelineResponseSchema = z.object({
    context: timelineContextSchema,
    items: z.array(timelineItemSchema),
    nextCursor: z.string().datetime().optional(),
});
export const createFinancialEventSchema = z.object({
    type: financialEventTypeSchema,
    status: financialEventStatusSchema.optional(),
    title: z.string().trim().min(1).max(160).optional(),
    note: z.string().trim().max(1000).optional(),
    amountMinor: amountMinorSchema.optional(),
    currency: currencySchema.optional(),
    sourceAmountMinor: amountMinorSchema.optional(),
    sourceCurrency: currencySchema.optional(),
    targetAmountMinor: amountMinorSchema.optional(),
    targetCurrency: currencySchema.optional(),
    fromAccountId: uuidSchema.optional(),
    toAccountId: uuidSchema.optional(),
    counterpartyUserId: uuidSchema.optional(),
    loanId: uuidSchema.optional(),
    categoryId: uuidSchema.optional(),
    occurredAt: z.coerce.date().optional(),
    confirmationPolicy: confirmationPolicySchema.default("none"),
    pendingConfirmationUserId: uuidSchema.optional(),
    blocks: z.array(timelineBlockSchema).max(20).default([]),
    metadata: z.record(z.unknown()).default({}),
});
export const timelineQuerySchema = paginationSchema.extend({
    scope: timelineScopeSchema.default("global"),
    id: uuidSchema.optional(),
});
export const timelineScopedQuerySchema = paginationSchema;
export const updateFinancialEventStatusSchema = z.object({
    note: z.string().trim().max(1000).optional(),
});
export const createEventAttachmentSchema = z.object({
    type: z.enum(["receipt", "screenshot", "document"]).default("receipt"),
    mimeType: z.string().trim().min(1).max(120).refine((value) => value.startsWith("image/"), {
        message: "only image evidence is supported in this alpha",
    }),
    dataBase64: z.string().min(1).max(6_000_000),
    sizeBytes: z.number().int().positive().max(4_000_000).optional(),
    width: z.number().int().positive().max(20_000).optional(),
    height: z.number().int().positive().max(20_000).optional(),
    caption: z.string().trim().max(160).optional(),
});
// ─── Finance Assistant (AI chat) ──────────────────────────────────────────────
/** One turn of prior chat passed back so the assistant has conversation context. */
export const assistantTurnSchema = z.object({
    role: z.enum(["user", "assistant"]),
    content: z.string().max(8000),
});
function latestAssistantHistory(value) {
    return Array.isArray(value) ? value.slice(-20) : value;
}
function validConversationIdOrUnset(value) {
    if (typeof value !== "string")
        return undefined;
    const trimmed = value.trim();
    if (!trimmed)
        return undefined;
    return uuidSchema.safeParse(trimmed).success ? trimmed : undefined;
}
/**
 * How much the assistant may do with the user's ledger, set per-request from the
 * client's AI-permission setting. `read` = answer only; `read_add` = may also
 * create entries; `full` = may create, edit, and delete. Defaults to `read`.
 */
export const AI_MODES = ["read", "read_add", "full"];
export const aiModeSchema = z.enum(AI_MODES).default("read");
export const assistantChatSchema = z.object({
    message: z.string().trim().min(1).max(2000),
    history: z.preprocess(latestAssistantHistory, z.array(assistantTurnSchema).optional()),
    // When set, the turn is appended to (and answered in the context of) an
    // existing saved conversation; omitted = start a new conversation.
    conversationId: z.preprocess(validConversationIdOrUnset, uuidSchema.optional()),
    mode: aiModeSchema.optional(),
});
export { transactionTypeSchema };
//# sourceMappingURL=schemas.js.map